import translate from 'core/translate'
import { listItemHeight, listItemSlotWidth, listSizeFor } from './placementMetrics'
import { EditorTheme } from '../templateEditor.theme'
import { ListElement, TableElement } from '../templateEditor.types'

const MIN_TRACK = 8

const emptyTableRow = (cols: number) => Array.from({ length: cols }, () => '')

const sum = (values: number[]) => values.reduce((acc, value) => acc + value, 0)

const clampIndex = (index: number, max: number) => Math.max(0, Math.min(max, index))

const insertAt = <T>(items: T[], index: number, item: T) => [...items.slice(0, index), item, ...items.slice(index)]

const getTracks = (explicit: number[] | undefined, count: number, fallback: number) => {
    if (explicit && explicit.length === count) {
        return explicit.map((value) => Math.max(MIN_TRACK, value))
    }
    const uniform = Math.max(MIN_TRACK, fallback)
    return Array.from({ length: count }, () => uniform)
}

/** Высоты строк: из массива или равномерный fallback. */
export const getTableRowHeights = (element: TableElement): number[] =>
    getTracks(element.rowHeights, element.rows, element.rowHeight || element.h / Math.max(1, element.rows))

/** Ширины столбцов: из массива или равномерный fallback. */
export const getTableColWidths = (element: TableElement): number[] =>
    getTracks(element.colWidths, element.cols, element.colWidth || element.w / Math.max(1, element.cols))

const withSyncedBounds = (element: TableElement, rowHeights: number[], colWidths: number[]): TableElement => ({
    ...element,
    rowHeights,
    colWidths,
    rowHeight: rowHeights[0] ?? element.rowHeight,
    colWidth: colWidths[0] ?? element.colWidth,
    h: Math.max(MIN_TRACK, sum(rowHeights)),
    w: Math.max(MIN_TRACK, sum(colWidths)),
})

export const insertTableRow = (element: TableElement, index: number): TableElement => {
    const at = clampIndex(index, element.rows)
    const rowHeights = getTableRowHeights(element)
    const insertH = element.rowHeight || rowHeights[Math.min(at, rowHeights.length - 1)] || MIN_TRACK

    return withSyncedBounds(
        { ...element, rows: element.rows + 1, data: insertAt(element.data, at, emptyTableRow(element.cols)) },
        insertAt(rowHeights, at, insertH),
        getTableColWidths(element)
    )
}

export const insertTableCol = (element: TableElement, index: number): TableElement => {
    const at = clampIndex(index, element.cols)
    const colWidths = getTableColWidths(element)
    const insertW = element.colWidth || colWidths[Math.min(at, colWidths.length - 1)] || MIN_TRACK

    return withSyncedBounds(
        { ...element, cols: element.cols + 1, data: element.data.map((row) => insertAt(row, at, '')) },
        getTableRowHeights(element),
        insertAt(colWidths, at, insertW)
    )
}

export const removeTableRow = (element: TableElement, index: number): TableElement => {
    if (element.rows <= 1 || index < 0 || index >= element.rows) {
        return element
    }

    return withSyncedBounds(
        { ...element, rows: element.rows - 1, data: element.data.filter((_, i) => i !== index) },
        getTableRowHeights(element).filter((_, i) => i !== index),
        getTableColWidths(element)
    )
}

export const removeTableCol = (element: TableElement, index: number): TableElement => {
    if (element.cols <= 1 || index < 0 || index >= element.cols) {
        return element
    }

    return withSyncedBounds(
        { ...element, cols: element.cols - 1, data: element.data.map((row) => row.filter((_, i) => i !== index)) },
        getTableRowHeights(element),
        getTableColWidths(element).filter((_, i) => i !== index)
    )
}

const resizeTrackAt = (tracks: number[], borderIndex: number, deltaLogical: number) => {
    const index = borderIndex <= 0 ? 0 : Math.min(tracks.length - 1, borderIndex - 1)
    const prev = tracks[index]
    const next = Math.max(MIN_TRACK, prev + (borderIndex <= 0 ? -deltaLogical : deltaLogical))
    const nextTracks = tracks.map((value, i) => (i === index ? next : value))
    return { tracks: nextTracks, originShift: borderIndex <= 0 ? next - prev : 0 }
}

/** Точечный ресайз строки. borderIndex: 0 = верх таблицы, N = низ. */
export const resizeTableRowAt = (element: TableElement, borderIndex: number, deltaLogical: number): TableElement => {
    const { tracks, originShift } = resizeTrackAt(getTableRowHeights(element), borderIndex, deltaLogical)
    const patched = withSyncedBounds(element, tracks, getTableColWidths(element))
    return originShift ? { ...patched, y: element.y - originShift } : patched
}

/** Точечный ресайз столбца. borderIndex: 0 = левый край, N = правый. */
export const resizeTableColAt = (element: TableElement, borderIndex: number, deltaLogical: number): TableElement => {
    const { tracks, originShift } = resizeTrackAt(getTableColWidths(element), borderIndex, deltaLogical)
    const patched = withSyncedBounds(element, getTableRowHeights(element), tracks)
    return originShift ? { ...patched, x: element.x - originShift } : patched
}

const scaleTracksTo = (values: number[], target: number) => {
    const total = sum(values) || 1
    const scaled = values.map((value) => Math.max(MIN_TRACK, (value / total) * target))
    const nextTotal = sum(scaled) || 1
    if (Math.abs(nextTotal - target) < 0.01) {
        return scaled
    }
    const last = scaled.length - 1
    return scaled.map((value, index) => (index === last ? Math.max(MIN_TRACK, value + (target - nextTotal)) : value))
}

/** Пропорциональный scale треков при bbox-ресайзе. */
export const scaleTableTracksToBox = (element: TableElement, nextW: number, nextH: number): TableElement =>
    withSyncedBounds(
        { ...element, w: nextW, h: nextH },
        scaleTracksTo(getTableRowHeights(element), nextH),
        scaleTracksTo(getTableColWidths(element), nextW)
    )

const withListItems = (element: ListElement, items: string[], theme: EditorTheme): ListElement => {
    const fit = listSizeFor(
        items.length,
        { fontSize: element.fontSize, gap: element.gap, orientation: element.orientation },
        theme
    )
    const horizontal = element.orientation === 'horizontal'
    return {
        ...element,
        items,
        w: horizontal ? fit.w : element.w,
        h: horizontal ? element.h : fit.h,
    }
}

export const insertListItem = (element: ListElement, index: number, theme: EditorTheme): ListElement => {
    const at = clampIndex(index, element.items.length)
    const label =
        element.orientation === 'horizontal'
            ? translate('templateEditorDefaultListText')
            : translate('templateEditorDefaultListItem')
    return withListItems(element, insertAt(element.items, at, `${label} ${at + 1}`), theme)
}

export const removeListItem = (element: ListElement, index: number, theme: EditorTheme): ListElement => {
    if (element.items.length <= 1 || index < 0 || index >= element.items.length) {
        return element
    }
    return withListItems(
        element,
        element.items.filter((_, i) => i !== index),
        theme
    )
}

const hitTrack = (sizes: number[], pos: number) => {
    let acc = 0
    for (let i = 0; i < sizes.length; i++) {
        if (pos < acc + sizes[i]) {
            return i
        }
        acc += sizes[i]
    }
    return Math.max(0, sizes.length - 1)
}

/** Индекс строки/столбца по локальным логическим координатам внутри таблицы. */
export const hitTableCell = (element: TableElement, localX: number, localY: number) => ({
    row: hitTrack(getTableRowHeights(element), localY),
    col: hitTrack(getTableColWidths(element), localX),
})

/** Индекс пункта списка по локальным логическим координатам. */
export const hitListItem = (element: ListElement, localX: number, localY: number, theme: EditorTheme) => {
    const count = Math.max(1, element.items.length)
    const step =
        element.orientation === 'horizontal'
            ? listItemSlotWidth(theme) + element.gap
            : listItemHeight({ fontSize: element.fontSize, lineHeight: 1.5, gap: element.gap }, theme)
    const pos = element.orientation === 'horizontal' ? localX : localY
    return clampIndex(Math.floor(pos / Math.max(1, step)), count - 1)
}
